// src/services/reviewService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const reviewApi = axios.create({
  baseURL: `${API_URL}/api/v1/reviews`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000
});

reviewApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('kapta_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const reviewService = {
  async getProductReviews(slug, { page = 1, limit = 10, sort = 'recent' } = {}) {
    const res = await reviewApi.get(`/product/${slug}`, {
      params: { page, limit, sort }
    });
    return res.data;
  },

  async createReview(payload) {
    const res = await reviewApi.post('/', payload);
    return res.data;
  },

  async updateReview(id, payload) {
    const res = await reviewApi.patch(`/${id}`, payload);
    return res.data;
  },

  async deleteReview(id) {
    const res = await reviewApi.delete(`/${id}`);
    return res.data;
  },

  async markHelpful(id) {
    const res = await reviewApi.post(`/${id}/helpful`);
    return res.data;
  },

  async getUserReviews(userId, { page = 1, limit = 10 } = {}) {
    const res = await reviewApi.get(`/user/${userId}`, { params: { page, limit } });
    return res.data;
  },

  /**
   * Upload a single file to Cloudinary (direct upload)
   * Returns { url, publicId, type }
   */
  async uploadImageToCloudinary(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append(
      'upload_preset',
      process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
    );

    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/auto/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      }
    );

    return {
      url: response.data.secure_url,
      publicId: response.data.public_id,
      type: response.data.resource_type === 'video' ? 'video' : 'image'
    };
  },

  /**
 * Upload an audio file to Cloudinary.
 * Cloudinary treats audio as `video` resource type.
 * Returns { url, publicId, type }
 */
async uploadAudioToCloudinary(file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append(
    'upload_preset',
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
  );
  formData.append('resource_type', 'video'); // enables audio

  const response = await axios.post(
    `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/video/upload`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000 // audio files can be bigger
    }
  );

  return {
    url: response.data.secure_url,
    publicId: response.data.public_id,
    type: 'audio',
    duration: response.data.duration || null
  };
}
};

export default reviewService;